import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  CardActions,
  Chip,
  Grid,
  Alert,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add, Work, Person, AttachMoney } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';

import { useAuth } from '../contexts/AuthContext.tsx';
import { projectsApi, customersApi } from '../services/api.ts';
import { Project, ProjectStatus, ProjectType, ProjectCreate } from '../types/index.ts';

// Kanban column configuration
const KANBAN_COLUMNS = [
  { id: 'lead', title: 'Leads', status: ProjectStatus.LEAD },
  { id: 'qualified', title: 'Qualified', status: ProjectStatus.QUALIFIED },
  { id: 'survey_scheduled', title: 'Survey Scheduled', status: ProjectStatus.SURVEY_SCHEDULED },
  { id: 'survey_completed', title: 'Survey Complete', status: ProjectStatus.SURVEY_COMPLETED },
  { id: 'quoted', title: 'Quoted', status: ProjectStatus.QUOTED },
  { id: 'approved', title: 'Approved', status: ProjectStatus.APPROVED },
  { id: 'installing', title: 'Installing', status: ProjectStatus.INSTALLING },
  { id: 'completed', title: 'Completed', status: ProjectStatus.COMPLETED },
];

// Project card component
interface ProjectCardProps {
  project: Project;
  onEdit: (project: Project) => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onEdit }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'project',
    item: { id: project.id, currentStatus: project.status },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.COMPLETED:
        return 'success';
      case ProjectStatus.INSTALLING:
        return 'warning';
      case ProjectStatus.CANCELLED:
        return 'error';
      case ProjectStatus.QUOTED:
        return 'info';
      default:
        return 'primary';
    }
  };

  const getTypeColor = (type: ProjectType) => {
    switch (type) {
      case ProjectType.SOLAR_PV:
        return '#ff9800';
      case ProjectType.HEAT_PUMP:
        return '#2196f3';
      case ProjectType.EV_CHARGING:
        return '#4caf50';
      case ProjectType.BATTERY_STORAGE:
        return '#9c27b0';
      case ProjectType.COMBINED:
        return '#f44336';
      default:
        return '#757575';
    }
  };

  return (
    <Card
      ref={drag}
      sx={{
        mb: 2,
        cursor: 'move',
        opacity: isDragging ? 0.5 : 1,
        border: isDragging ? '2px dashed #ccc' : 'none',
        '&:hover': {
          boxShadow: 2,
        },
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="h3" sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
            {project.title}
          </Typography>
          <Chip
            label={project.project_type_display}
            size="small"
            sx={{
              backgroundColor: getTypeColor(project.project_type),
              color: 'white',
              fontSize: '0.75rem',
            }}
          />
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          {project.project_number}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Customer ID: {project.customer_id}
          </Typography>
        </Box>

        {project.estimated_cost && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AttachMoney sx={{ fontSize: 16, color: 'success.main' }} />
            <Typography variant="body2" color="success.main" fontWeight={600}>
              £{project.estimated_cost.toLocaleString()}
            </Typography>
          </Box>
        )}

        {project.system_size && (
          <Typography variant="body2" color="text.secondary">
            {project.system_size}
          </Typography>
        )}
      </CardContent>

      <CardActions sx={{ pt: 0 }}>
        <Button size="small" onClick={() => onEdit(project)}>
          View Details
        </Button>
      </CardActions>
    </Card>
  );
};

// Kanban column component
interface KanbanColumnProps {
  column: typeof KANBAN_COLUMNS[0];
  projects: Project[];
  onEdit: (project: Project) => void;
  onMoveProject: (projectId: number, newStatus: ProjectStatus) => void;
}

const KanbanColumn: React.FC<KanbanColumnProps> = ({ column, projects, onEdit, onMoveProject }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'project',
    drop: (item: { id: number; currentStatus: ProjectStatus }) => {
      if (item.currentStatus !== column.status) {
        onMoveProject(item.id, column.status);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  return (
    <Paper
      ref={drop}
      sx={{
        p: 2,
        minHeight: 500,
        backgroundColor: isOver ? 'action.hover' : 'background.paper',
        border: isOver ? '2px dashed primary.main' : '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h2">
          {column.title}
        </Typography>
        <Chip label={projects.length} size="small" color="primary" />
      </Box>

      {projects.map((project) => (
        <ProjectCard key={project.id} project={project} onEdit={onEdit} />
      ))}

      {projects.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No projects
        </Typography>
      )}
    </Paper>
  );
};

// Add project dialog component
interface AddProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (project: ProjectCreate) => void;
}

const AddProjectDialog: React.FC<AddProjectDialogProps> = ({ open, onClose, onAdd }) => {
  const { control, handleSubmit, reset, formState: { errors } } = useForm<ProjectCreate>();
  const { data: customers } = useQuery('customers', () => customersApi.getCustomers());

  const onSubmit = (data: ProjectCreate) => {
    onAdd(data);
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add New Project</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="title"
                control={control}
                rules={{ required: 'Title is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Project Title"
                    fullWidth
                    error={!!errors.title}
                    helperText={errors.title?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="customer_id"
                control={control}
                rules={{ required: 'Customer is required' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.customer_id}>
                    <InputLabel>Customer</InputLabel>
                    <Select {...field} label="Customer">
                      {customers?.customers.map((customer) => (
                        <MenuItem key={customer.id} value={customer.id}>
                          {customer.full_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="project_type"
                control={control}
                rules={{ required: 'Project type is required' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.project_type}>
                    <InputLabel>Project Type</InputLabel>
                    <Select {...field} label="Project Type">
                      <MenuItem value={ProjectType.SOLAR_PV}>Solar PV</MenuItem>
                      <MenuItem value={ProjectType.HEAT_PUMP}>Heat Pump</MenuItem>
                      <MenuItem value={ProjectType.EV_CHARGING}>EV Charging</MenuItem>
                      <MenuItem value={ProjectType.BATTERY_STORAGE}>Battery Storage</MenuItem>
                      <MenuItem value={ProjectType.COMBINED}>Combined System</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description"
                    multiline
                    rows={3}
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="estimated_cost"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Estimated Cost (£)"
                    type="number"
                    fullWidth
                    onChange={(e) => field.onChange(parseFloat(e.target.value) || undefined)}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="system_size"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="System Size"
                    placeholder="e.g., 4.0kW"
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notes"
                    multiline
                    rows={2}
                    fullWidth
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            Add Project
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const ProjectsPage: React.FC = () => {
  const { user, isManager, isSurveyor } = useAuth();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  // Fetch projects
  const { data: projectsData, isLoading, error } = useQuery(
    'projects',
    () => projectsApi.getProjects(),
    {
      refetchOnWindowFocus: false,
    }
  );

  // Update project status mutation
  const updateProjectMutation = useMutation(
    ({ id, status }: { id: number; status: ProjectStatus }) =>
      projectsApi.updateProject(id, { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('projects');
      },
    }
  );

  // Add project mutation
  const addProjectMutation = useMutation(
    (project: ProjectCreate) => projectsApi.createProject(project),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('projects');
      },
    }
  );

  const handleMoveProject = (projectId: number, newStatus: ProjectStatus) => {
    updateProjectMutation.mutate({ id: projectId, status: newStatus });
  };

  const handleAddProject = (project: ProjectCreate) => {
    addProjectMutation.mutate(project);
  };

  const handleEditProject = (project: Project) => {
    // For now, just log - could open edit dialog
    console.log('Edit project:', project);
  };

  // Organize projects by status
  const projectsByStatus = React.useMemo(() => {
    if (!projectsData?.projects) return {};

    return projectsData.projects.reduce((acc, project) => {
      if (!acc[project.status]) {
        acc[project.status] = [];
      }
      acc[project.status].push(project);
      return acc;
    }, {} as Record<ProjectStatus, Project[]>);
  }, [projectsData?.projects]);

  if (error) {
    return (
      <Alert severity="error">
        Failed to load projects. Please try refreshing the page.
      </Alert>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Projects
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {projectsData?.total || 0} total projects
            </Typography>
          </Box>
          {(isManager || isSurveyor) && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setAddDialogOpen(true)}
            >
              New Project
            </Button>
          )}
        </Box>

        {isLoading ? (
          <Grid container spacing={2}>
            {KANBAN_COLUMNS.map((column) => (
              <Grid item xs={12} sm={6} md={3} lg={1.5} key={column.id}>
                <Skeleton variant="rectangular" height={400} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ overflowX: 'auto', pb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, minWidth: 1200 }}>
              {KANBAN_COLUMNS.map((column) => (
                <Box key={column.id} sx={{ minWidth: 280, flex: '1 0 280px' }}>
                  <KanbanColumn
                    column={column}
                    projects={projectsByStatus[column.status] || []}
                    onEdit={handleEditProject}
                    onMoveProject={handleMoveProject}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Add Project Dialog */}
        <AddProjectDialog
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          onAdd={handleAddProject}
        />
      </Box>
    </DndProvider>
  );
};

export default ProjectsPage;